import React, { useEffect, useState } from "react";
import Icon from "./Icon";
import InstallInstructions from "./InstallInstructions";
import { useI18n } from "../i18n";
import { C, R, S } from "../theme";

const DISMISS_KEY = "sb_install_dismissed";

export default function InstallPrompt() {
  const { t: tr } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(localStorage.getItem(DISMISS_KEY) === "1");
  const [howOpen, setHowOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  const ua = window.navigator.userAgent || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isMobile = isIOS || isAndroid;

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (isStandalone || dismissed || !isMobile) return null;

  const confirmAdded = () => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); setHowOpen(false); };
  const nativeInstall = async () => {
    if (!deferred) { setHowOpen(true); return; }
    deferred.prompt();
    const res = await deferred.userChoice;
    setDeferred(null);
    if (res && res.outcome === "accepted") confirmAdded();
    else setHowOpen(true);
  };

  return (
    <>
      <div data-testid="install-banner" style={{ background: C.card, border: `1px solid ${C.amber}`, borderRadius: R.md, padding: S.md, marginTop: S.md }}>
        <div style={{ display: "flex", alignItems: "center", gap: S.md }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: C.amber, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Icon name="shield-checkmark" size={22} color={C.black} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ color: C.text, fontWeight: 800, fontSize: 14 }}>{tr("install.appTitle")}</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 2 }}>{tr("install.bannerSub")} ({isIOS ? "iPhone/iPad" : "Android"}).</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: S.sm, marginTop: S.md }}>
          {deferred && (
            <button data-testid="install-native-btn" className="pressable" onClick={nativeInstall} style={{ flex: 1, background: C.amber, color: C.black, fontWeight: 900, borderRadius: R.md, minHeight: 46, fontSize: 14 }}>{tr("install.installNow")}</button>
          )}
          <button data-testid="install-how" className="pressable" onClick={() => setHowOpen(true)} style={{ flex: 1, background: deferred ? C.card2 : C.amber, color: deferred ? C.amber : C.black, fontWeight: 800, borderRadius: R.md, minHeight: 46, fontSize: 14, border: deferred ? `1px solid ${C.borderStrong}` : "none" }}>{tr("install.showHow")}</button>
        </div>
        <label data-testid="install-confirm-label" style={{ display: "flex", alignItems: "center", gap: S.sm, marginTop: S.md, cursor: "pointer" }}>
          <input data-testid="install-confirm-checkbox" aria-label="I have added SafeBook to my home screen" type="checkbox" checked={confirmChecked} onChange={(e) => { setConfirmChecked(e.target.checked); if (e.target.checked) confirmAdded(); }} style={{ width: 18, height: 18, accentColor: C.amber }} />
          <span style={{ color: C.sub, fontSize: 13 }}>{tr("install.confirmAdded")}</span>
        </label>
      </div>

      <InstallInstructions open={howOpen} onClose={() => setHowOpen(false)} onAdded={confirmAdded} deferredInstall={deferred ? nativeInstall : null} />
    </>
  );
}
